import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useFormContext, useWatch } from 'react-hook-form';
import { getAsYouType } from 'awesome-phonenumber';
import { SelectOption } from '../../../types/common';
import LabeledTextbox from '../LabeledTextbox';
import LabeledCombobox from '../LabeledCombobox';
import LabeledDropdown from '../LabeledDropdown';
import LabeledCheckbox from '../LabeledCheckbox';
import { CountryAndStateSelectOption } from '../../../types/Configuration';
import { DefaultValues } from '../../../utils/constants';

export const AddressFormTestId = 'addressform-input';

export type AddressFormProps = {
  countryOptions: CountryAndStateSelectOption[];
  isShippingAddress?: boolean;
};

const AddressFormWithFormContext: React.FC<AddressFormProps> = ({
  countryOptions,
  isShippingAddress = false,
}: AddressFormProps) => {
  const {
    formState: { errors },
    register,
    setValue,
    getValues,
    control,
  } = useFormContext<{
    billingAddress: {
      firstName: string;
      lastName: string;
      companyName: string;
      address1: string;
      address2: string;
      city: string;
      state: string;
      country: string;
      postalCode: string;
      phoneNumber: string;
    };
  }>();

  const [countryCode, setCountryCode] = useState('');

  const countrySelectOptions = useRef<SelectOption[]>(
    countryOptions?.map(({ countryLabel, countryValue }) => ({
      label: countryLabel,
      value: countryValue,
    }))
  );

  const countryWatch = useWatch({
    control,
    name: 'billingAddress.country',
  });

  const stateLabel =
    countryOptions.find(country => country.countryValue === countryWatch)
      ?.stateLabel ?? 'State';

  const stateOptions =
    countryOptions.find(country => country.countryValue === countryWatch)
      ?.stateValues ?? [];

  useEffect(() => {
    if (countryWatch === undefined)
      setValue(
        'billingAddress.country',
        DefaultValues.DEFAULT_SHIPPING_COUNTRY
      );
    const options =
      countryOptions.find(country => country.countryValue === countryWatch)
        ?.stateValues ?? [];
    const currentStateValue = getValues('billingAddress.state');
    if (
      !currentStateValue ||
      !options.some(option => option.value === currentStateValue)
    ) {
      if (options.length && !currentStateValue) {
        setValue('billingAddress.state', options[0].value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryWatch]);

  const getCountryCode = async () => {
    try {
      const response = await axios.get('https://ipapi.co/json/');

      if (response?.data?.country_code) {
        setCountryCode(response.data.country_code);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching the country code:', error);
    }
  };

  useEffect(() => {
    getCountryCode();
  }, []);

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    const formattedNumber = getAsYouType(countryCode || 'US');
    formattedNumber.reset(input.replace(/[^+0-9]/g, ''));

    if (formattedNumber) {
      return formattedNumber?.getPhoneNumber()?.number?.input || input;
    }
    return '';
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:justify-items-stretch w-full">
        <LabeledTextbox
          name="billingAddress.firstName"
          label="First name"
          autoComplete="given-name"
          className="mr-2.5 mb-0 md:w-1/2"
          errorLabel={errors?.billingAddress?.firstName?.message}
          register={register}
        />
        <LabeledTextbox
          name="billingAddress.lastName"
          label="Last name"
          autoComplete="family-name"
          className="ml-2.5 mb-0 md:w-1/2"
          errorLabel={errors?.billingAddress?.lastName?.message}
          register={register}
        />
      </div>
      <div className="flex flex-col justify-items-stretch w-full">
        <LabeledTextbox
          className="mb-0"
          name="billingAddress.companyName"
          label="Company (optional)"
          autoComplete="organization"
          errorLabel={errors?.billingAddress?.companyName?.message}
          register={register}
        />
        <LabeledTextbox
          className="mb-0"
          name="billingAddress.address1"
          label="Address"
          autoComplete="address-line1"
          errorLabel={errors?.billingAddress?.address1?.message}
          register={register}
        />
        <LabeledTextbox
          className="mb-0"
          name="billingAddress.address2"
          label="Apartment, Building, Floor (optional)"
          autoComplete="address-line2"
          errorLabel={errors?.billingAddress?.address2?.message}
          register={register}
        />
      </div>
      <div className="flex flex-col md:flex-row md:justify-items-stretch w-full">
        <LabeledTextbox
          name="billingAddress.city"
          label="City"
          autoComplete="address-level2"
          className="mr-2.5 mb-0 md:w-1/2"
          errorLabel={errors?.billingAddress?.city?.message}
          register={register}
        />
        <LabeledDropdown
          name="billingAddress.country"
          label="Country"
          autoComplete="country"
          className="ml-2.5 md:w-1/2"
          errorLabel={errors?.billingAddress?.country?.message}
          defaultValue={countryWatch}
          options={countrySelectOptions.current}
          register={register}
        />
      </div>
      <div className="flex flex-col md:flex-row md:justify-items-stretch w-full">
        <LabeledTextbox
          name="billingAddress.postalCode"
          label="ZIP code"
          autoComplete="postal-code"
          className="mr-2.5 md:w-1/2"
          errorLabel={errors?.billingAddress?.postalCode?.message}
          register={register}
        />
        <LabeledCombobox
          name="billingAddress.state"
          label={stateLabel}
          autoComplete="address-level1"
          className="ml-2.5 mb-0 md:w-1/2"
          errorLabel={errors?.billingAddress?.state?.message}
          options={stateOptions}
          register={register}
        />
      </div>
      {isShippingAddress && (
        <LabeledCheckbox
          className="mb-4"
          name="billingAddress.residential"
          label="Residential Address"
          register={register}
        />
      )}
      <LabeledTextbox
        name="billingAddress.phoneNumber"
        label="Phone"
        type="tel"
        autoComplete="tel"
        maxLength={30}
        pattern="^[\d\s\+\(\)\-\*\.#extn]{7,31}"
        errorLabel={errors?.billingAddress?.phoneNumber?.message}
        register={register}
        handlePhoneInputChange={handlePhoneInputChange}
      />
    </div>
  );
};

export default AddressFormWithFormContext;
